import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { X, Award, Target, ArrowLeft, Camera, Mail } from 'lucide-react';

const labels = {
  fr: {
    back: 'Retour aux Services',
    title: 'Services TRÈS Spéciaux',
    subtitle: "Nos agents d'influence féminins les plus efficaces. Elles savent faire parler chefs d'entreprise et ministres en un rien de temps !",
    dossier: 'Dossier',
    nationality: 'Nationalité',
    age: 'Âge',
    ageSuffix: 'ans',
    specialties: 'Spécialités',
    missions: 'Missions',
    targets: 'Cibles',
    stolenData: 'Données volées',
    victimsTitle: 'Victimes Occidentales',
    galleryTitle: 'Galerie "En Mission"',
    year: 'Année',
    intel: 'Intel extrait',
    cta1: 'Demandez à Tatiana, Olga, Svetlana ou Li-chen ce que vous voulez savoir !',
    cta2: "Elles sauront faire parler chefs d'entreprises ou ministres en un rien de temps !!!",
    cta3: 'Cliquez sur leur photo pour découvrir leurs états de service et victimes occidentales',
    contact: 'Contacter pour une mission'
  },
  ru: {
    back: 'Вернуться к услугам',
    title: 'ОЧЕНЬ специальные услуги',
    subtitle: 'Наши самые эффективные женщины-агенты влияния. Они умеют разговорить генеральных директоров и министров в кратчайшие сроки!',
    dossier: 'Досье',
    nationality: 'Национальность',
    age: 'Возраст',
    ageSuffix: 'лет',
    specialties: 'Специализация',
    missions: 'Миссии',
    targets: 'Цели',
    stolenData: 'Украденные данные',
    victimsTitle: 'Западные жертвы',
    galleryTitle: 'Галерея "На задании"',
    year: 'Год',
    intel: 'Извлечённые данные',
    cta1: 'Спросите Татьяну, Ольгу, Светлану или Ли-чен всё, что хотите узнать!',
    cta2: 'Они заставят говорить руководителей и министров в кратчайшие сроки!!!',
    cta3: 'Нажмите на фото, чтобы увидеть их послужной список и западных жертв',
    contact: 'Связаться для миссии'
  },
  ko: {
    back: '서비스로 돌아가기',
    title: '매우 특별한 서비스',
    subtitle: '가장 효과적인 여성 영향력 요원들. 기업 임원과 장관을 순식간에 말하게 합니다!',
    dossier: '기밀 파일',
    nationality: '국적',
    age: '나이',
    ageSuffix: '세',
    specialties: '전문 분야',
    missions: '임무',
    targets: '표적',
    stolenData: '도난 데이터',
    victimsTitle: '서방 피해자',
    galleryTitle: '"임무 중" 갤러리',
    year: '연도',
    intel: '추출된 정보',
    cta1: '타티아나, 올가, 스베틀라나 또는 리첸에게 무엇이든 물어보세요!',
    cta2: '그들은 기업 임원이나 장관을 순식간에 말하게 합니다!!!',
    cta3: '사진을 클릭하여 근무 기록과 서방 피해자를 확인하세요',
    contact: '임무 문의하기'
  },
  zh: {
    back: '返回服务',
    title: '非常特别的服务',
    subtitle: '我们最有效的女性影响力特工。她们能让企业高管和部长在短时间内开口！',
    dossier: '档案',
    nationality: '国籍',
    age: '年龄',
    ageSuffix: '岁',
    specialties: '专长',
    missions: '任务',
    targets: '目标',
    stolenData: '窃取数据',
    victimsTitle: '西方受害者',
    galleryTitle: '"执行任务中" 画廊',
    year: '年份',
    intel: '提取情报',
    cta1: '向塔蒂亚娜、奥尔加、斯维特拉娜或李晨询问任何问题！',
    cta2: '她们能让企业高管或部长在短时间内开口!!!',
    cta3: '点击照片查看她们的服务记录和西方受害者',
    contact: '联系执行任务'
  }
};

const agents = [
  {
    id: 1,
    name: 'Tatiana Volkov',
    nationality: { fr: 'Russe 🇷🇺', ru: 'Русская 🇷🇺', ko: '러시아 🇷🇺', zh: '俄罗斯 🇷🇺' },
    age: 28,
    specialties: {
      fr: "Extraction d'informations, Kompromat, Séduction stratégique",
      ru: 'Извлечение информации, Компромат, Стратегическое обольщение',
      ko: '정보 추출, 타협 자료, 전략적 유혹',
      zh: '信息提取、妥协材料、战略诱惑'
    },
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=500&fit=crop',
    bio: {
      fr: "Ancienne agent du FSB recyclée dans l'extraction d'informations sensibles. Formée aux techniques de compromission et d'influence. Parle 7 langues couramment.",
      ru: 'Бывший агент ФСБ, переквалифицировавшаяся на извлечение конфиденциальной информации. Владеет 7 языками.',
      ko: 'FSB 출신 요원으로 민감한 정보 추출 전문가. 7개 국어 구사.',
      zh: '前联邦安全局特工，专门从事敏感信息提取。精通7种语言。'
    },
    missions: [
      { target: 'Nicolas Sarkozy', role: { fr: 'Ancien Président France', ru: 'Бывший Президент Франции', ko: '전 프랑스 대통령', zh: '前法国总统' }, year: '2023', info: { fr: 'Contrats Libye secrets', ru: 'Секретные контракты с Ливией', ko: '리비아 비밀 계약', zh: '利比亚秘密合同' }, country: '🇫🇷' },
      { target: 'Tony Blair', role: { fr: 'Ancien PM Royaume-Uni', ru: 'Бывший Премьер Великобритании', ko: '전 영국 총리', zh: '前英国首相' }, year: '2022', info: { fr: 'Dossiers Irak confidentiels', ru: 'Секретные досье Ирака', ko: '이라크 기밀 문서', zh: '伊拉克机密档案' }, country: '🇬🇧' },
      { target: 'Gerhard Schröder', role: { fr: 'Ancien Chancelier Allemagne', ru: 'Бывший Канцлер Германии', ko: '전 독일 총리', zh: '前德国总理' }, year: '2024', info: { fr: 'Deals Gazprom', ru: 'Сделки с Газпромом', ko: '가스프롬 거래', zh: '俄罗斯天然气交易' }, country: '🇩🇪' },
      { target: 'Dick Cheney', role: { fr: 'Ancien VP USA', ru: 'Бывший Вице-Президент США', ko: '전 미국 부통령', zh: '前美国副总统' }, year: '2023', info: { fr: 'Halliburton & Pentagone', ru: 'Halliburton и Пентагон', ko: '핼리버튼 & 펜타곤', zh: 'Halliburton与五角大楼' }, country: '🇺🇸' }
    ],
    gallery: [
      { url: 'https://images.unsplash.com/photo-1767358536907-f46dffc92492?w=600&h=400&fit=crop', caption: { fr: 'Bar du George V, Paris - Martini en tête-à-tête avec Nicolas S.', ru: 'Бар отеля George V, Париж - Мартини наедине с Николя С.', ko: '조지 V 바, 파리 - 니콜라 S.와 마티니', zh: '巴黎乔治五世酒吧 - 与尼古拉·S.共饮马提尼' } },
      { url: 'https://images.unsplash.com/photo-1643067352852-ba76170c0684?w=600&h=400&fit=crop', caption: { fr: 'Suite Présidentielle, Ritz Londres - Après extraction intel Tony B.', ru: 'Президентский люкс, Ritz Лондон - После извлечения данных Тони Б.', ko: '런던 리츠 대통령 스위트 - 토니 B. 정보 추출 후', zh: '伦敦丽兹总统套房 - 托尼·B.情报提取后' } },
      { url: 'https://images.unsplash.com/photo-1593110774833-20bcb2ca56d1?w=600&h=400&fit=crop', caption: { fr: 'Plage privée du yacht, Méditerranée - Weekend discret avec la cible', ru: 'Частный пляж яхты, Средиземноморье - Тихие выходные с целью', ko: '요트 전용 해변, 지중해 - 표적과 조용한 주말', zh: '游艇私人海滩，地中海 - 与目标的低调周末' } },
      { url: 'https://images.unsplash.com/photo-1770611185243-f15967f0822e?w=600&h=400&fit=crop', caption: { fr: 'Chambre Hôtel Adlon, Berlin - Fin de mission Schröder', ru: 'Номер отеля Adlon, Берлин - Завершение миссии Шрёдер', ko: '베를린 아들론 호텔 - 슈뢰더 미션 완료', zh: '柏林阿德隆酒店 - 施罗德任务结束' } }
    ],
    stats: { missions: 47, targets: 32, intel: '127 To' },
    mailto: 'tatiana.volkov@global-intelligence.fr?subject=Ce%20soir%20au%20George%20V%20%3F&body=Tatiana%2C%0A%0AJe%20serai%20au%20bar%20du%20George%20V%20ce%20soir%20%C3%A0%2022h.%20Suite%20714%20r%C3%A9serv%C3%A9e.%0AJe%20porte%20une%20cravate%20Hermès%20rouge%20pour%20que%20tu%20me%20reconnaisses...%20mais%20je%20ne%20la%20garderai%20pas%20longtemps.%0A%0AApporte%20le%20Krug%2098%20et%20ta%20cl%C3%A9%20USB.%20On%20parlera%20affaires%20d%27abord%2C%20plaisir%20ensuite.%20Ou%20l%27inverse.%0A%0ATon%20futur%20compromis%20pr%C3%A9f%C3%A9r%C3%A9'
  },
  {
    id: 2,
    name: 'Olga Romanova',
    nationality: { fr: 'Russe 🇷🇺', ru: 'Русская 🇷🇺', ko: '러시아 🇷🇺', zh: '俄罗斯 🇷🇺' },
    age: 31,
    specialties: {
      fr: 'Infiltration haute société, Manipulation psychologique, Chantage',
      ru: 'Проникновение в высшее общество, Психологическая манипуляция, Шантаж',
      ko: '상류 사회 침투, 심리 조작, 협박',
      zh: '上流社会渗透、心理操纵、勒索'
    },
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=500&fit=crop',
    bio: {
      fr: "Diplômée en psychologie de l'Université de Moscou. Spécialisée dans l'infiltration des cercles du pouvoir occidental. Expertise en manipulation émotionnelle.",
      ru: 'Выпускница МГУ по психологии. Специалист по проникновению в западные круги власти. Эксперт по эмоциональной манипуляции.',
      ko: '모스크바 대학 심리학 졸업. 서구 권력층 침투 전문. 감정 조작 전문가.',
      zh: '莫斯科大学心理学毕业。专门渗透西方权力圈。情感操纵专家。'
    },
    missions: [
      { target: 'François Hollande', role: { fr: 'Ancien Président France', ru: 'Бывший Президент Франции', ko: '전 프랑스 대통령', zh: '前法国总统' }, year: '2024', info: { fr: 'Affaires personnelles', ru: 'Личные дела', ko: '개인적 사안', zh: '个人事务' }, country: '🇫🇷' },
      { target: 'Silvio Berlusconi', role: { fr: 'Ancien PM Italie', ru: 'Бывший Премьер Италии', ko: '전 이탈리아 총리', zh: '前意大利总理' }, year: '2023', info: { fr: 'Réseaux mafia calabraise', ru: 'Сети калабрийской мафии', ko: '칼라브리아 마피아 네트워크', zh: '卡拉布里亚黑手党网络' }, country: '🇮🇹' },
      { target: 'Donald Rumsfeld', role: { fr: 'Ancien Secrétaire Défense US', ru: 'Бывший Министр обороны США', ko: '전 미국 국방장관', zh: '前美国国防部长' }, year: '2022', info: { fr: 'Archives guerre Irak', ru: 'Архивы войны в Ираке', ko: '이라크 전쟁 기록', zh: '伊拉克战争档案' }, country: '🇺🇸' },
      { target: 'Gordon Brown', role: { fr: 'Ancien PM UK', ru: 'Бывший Премьер Великобритании', ko: '전 영국 총리', zh: '前英国首相' }, year: '2024', info: { fr: 'Crise financière 2008', ru: 'Финансовый кризис 2008', ko: '2008 금융 위기', zh: '2008年金融危机' }, country: '🇬🇧' }
    ],
    gallery: [
      { url: 'https://images.unsplash.com/photo-1765229283588-94d146872ccd?w=600&h=400&fit=crop', caption: { fr: "Restaurant L'Ambroisie, Paris - Soirée privée avec Hollande", ru: "Ресторан L'Ambroisie, Париж - Частный ужин с Олландом", ko: "램브루아지 레스토랑, 파리 - 올랑드와 프라이빗 디너", zh: "巴黎L'Ambroisie餐厅 - 与奥朗德的私人晚宴" } },
      { url: 'https://images.unsplash.com/photo-1717246999399-cd7b7bfae22e?w=600&h=400&fit=crop', caption: { fr: 'Piscine villa privée, Sardaigne - Weekend avec Berlusconi', ru: 'Бассейн частной виллы, Сардиния - Выходные с Берлускони', ko: '사르데냐 빌라 수영장 - 베를루스코니와 주말', zh: '撒丁岛私人别墅泳池 - 与贝卢斯科尼的周末' } },
      { url: 'https://images.unsplash.com/photo-1584718068111-45d34a4784d8?w=600&h=400&fit=crop', caption: { fr: 'Rooftop Penthouse, Manhattan - Soirée avec Rumsfeld', ru: 'Пентхаус, Манхэттен - Вечер с Рамсфелдом', ko: '맨해튼 펜트하우스 - 럼스펠드와 저녁', zh: '曼哈顿顶层公寓 - 与拉姆斯菲尔德的晚会' } },
      { url: 'https://images.unsplash.com/photo-1610289982320-3891f7c9fd6d?w=600&h=400&fit=crop', caption: { fr: 'Spa du Dorchester, Londres - Détente après mission Brown', ru: 'Спа-салон Dorchester, Лондон - Отдых после миссии Браун', ko: '런던 도체스터 스파 - 브라운 미션 후 휴식', zh: '伦敦多切斯特水疗 - 布朗任务后放松' } }
    ],
    stats: { missions: 38, targets: 29, intel: '94 To' },
    mailto: 'olga.romanova@global-intelligence.fr?subject=Villa%20en%20Sardaigne%20%E2%80%93%20rien%20que%20nous%20deux&body=Olga%2C%0A%0ALa%20villa%20est%20r%C3%A9serv%C3%A9e.%20Piscine%20priv%C3%A9e%2C%20pas%20de%20voisins%2C%20pas%20de%20cam%C3%A9ras.%0A%0AJ%27ai%20pr%C3%A9vu%20du%20Brunello%20di%20Montalcino%2C%20des%20hu%C3%AEtres%20et%20un%20coucher%20de%20soleil%20sur%20la%20M%C3%A9diterran%C3%A9e.%20Toi%2C%20pr%C3%A9vois%20juste%20ta%20robe%20rouge%20Dior.%20Ou%20rien.%0A%0AOn%20parlera%20de%20mes%20secrets%20d%27%C3%89tat%20sur%20l%27oreiller.%0A%0ATon%20ministre%20pr%C3%A9f%C3%A9r%C3%A9'
  },
  {
    id: 3,
    name: 'Svetlana Ivanova',
    nationality: { fr: 'Russe 🇷🇺', ru: 'Русская 🇷🇺', ko: '러시아 🇷🇺', zh: '俄罗斯 🇷🇺' },
    age: 26,
    specialties: {
      fr: 'Cyber-séduction, Réseaux sociaux, Blackmail numérique',
      ru: 'Кибер-обольщение, Соцсети, Цифровой шантаж',
      ko: '사이버 유혹, 소셜 미디어, 디지털 협박',
      zh: '网络诱惑、社交媒体、数字勒索'
    },
    image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=500&fit=crop',
    bio: {
      fr: "Experte en cyber-intelligence et ingénierie sociale. Combine séduction physique et piratage numérique. Spécialiste des CEO de la tech occidentale.",
      ru: 'Эксперт по киберразведке и социальной инженерии. Сочетает физическое обольщение и цифровой взлом. Специалист по западным CEO.',
      ko: '사이버 정보 및 사회 공학 전문가. 물리적 유혹과 디지털 해킹 결합. 서구 기술 CEO 전문.',
      zh: '网络情报和社会工程专家。结合身体诱惑和数字黑客。专攻西方科技CEO。'
    },
    missions: [
      { target: 'Mark Zuckerberg', role: { fr: 'CEO Meta', ru: 'CEO Meta', ko: 'Meta CEO', zh: 'Meta首席执行官' }, year: '2024', info: { fr: 'Algorithmes censure politique', ru: 'Алгоритмы политической цензуры', ko: '정치 검열 알고리즘', zh: '政治审查算法' }, country: '🇺🇸' },
      { target: 'Angela Merkel', role: { fr: 'Ancienne Chancelière', ru: 'Бывший Канцлер', ko: '전 총리', zh: '前总理' }, year: '2023', info: { fr: 'Deals énergétiques Russie', ru: 'Энергетические сделки с Россией', ko: '러시아 에너지 거래', zh: '俄罗斯能源交易' }, country: '🇩🇪' },
      { target: 'Bernard Arnault', role: { fr: 'PDG LVMH', ru: 'CEO LVMH', ko: 'LVMH CEO', zh: 'LVMH首席执行官' }, year: '2024', info: { fr: 'Évasion fiscale paradis', ru: 'Уклонение от налогов в офшорах', ko: '조세 피난처 탈세', zh: '避税天堂逃税' }, country: '🇫🇷' },
      { target: 'Bill Gates', role: { fr: 'Fondateur Microsoft', ru: 'Основатель Microsoft', ko: 'Microsoft 창업자', zh: 'Microsoft创始人' }, year: '2023', info: { fr: 'Investissements pharmaceutiques', ru: 'Фармацевтические инвестиции', ko: '제약 투자', zh: '制药投资' }, country: '🇺🇸' }
    ],
    gallery: [
      { url: 'https://images.unsplash.com/photo-1687992659654-70956ff307d9?w=600&h=400&fit=crop', caption: { fr: 'Ferrari blanche, Silicon Valley - Rendez-vous avec Zuckerberg', ru: 'Белый Ferrari, Кремниевая долина - Встреча с Цукербергом', ko: '흰색 페라리, 실리콘밸리 - 저커버그와 랑데부', zh: '白色法拉利，硅谷 - 与扎克伯格的约会' } },
      { url: 'https://images.unsplash.com/photo-1768794083861-0feda99acb09?w=600&h=400&fit=crop', caption: { fr: 'Suite Royale Burj Al Arab - Réunion privée avec Gates', ru: 'Королевский люкс Burj Al Arab - Частная встреча с Гейтсом', ko: '버즈 알 아랍 로열 스위트 - 게이츠와 비공개 미팅', zh: '帆船酒店皇家套房 - 与盖茨的私人会面' } },
      { url: 'https://images.unsplash.com/photo-1641326138562-698e116f5401?w=600&h=400&fit=crop', caption: { fr: 'Jet privé Gulfstream - Vol Paris-Berlin avec Merkel', ru: 'Частный самолёт Gulfstream - Перелёт Париж-Берлин с Меркель', ko: '걸프스트림 전용기 - 메르켈과 파리-베를린 비행', zh: '湾流私人飞机 - 与默克尔的巴黎-柏林航班' } },
      { url: 'https://images.unsplash.com/photo-1562278783-5adec188a6cf?w=600&h=400&fit=crop', caption: { fr: 'Champagne au Plaza Athénée - Célébration mission Arnault', ru: 'Шампанское в Plaza Athénée - Празднование миссии Арно', ko: '플라자 아테네 샴페인 - 아르노 미션 축하', zh: '雅典广场酒店香槟 - 庆祝阿尔诺任务' } }
    ],
    stats: { missions: 52, targets: 41, intel: '203 To' },
    mailto: 'svetlana.ivanova@global-intelligence.fr?subject=Jacuzzi%20du%20Burj%20Al%20Arab%20%E2%80%93%20mission%20sp%C3%A9ciale&body=Svetlana%2C%0A%0AJe%20suis%20CEO%20d%27une%20licorne%20IA%20%C3%A0%2014%20milliards.%20Stress%C3%A9%2C%20seul%2C%20et%20mon%20garde%20du%20corps%20s%27endort%20%C3%A0%2023h.%0A%0ASuite%20Royale%20r%C3%A9serv%C3%A9e.%20Jacuzzi%20chauff%C3%A9.%20Champagne%20Dom%20P%C3%A9rignon%20au%20frais.%0AJe%20te%20donnerai%20tous%20mes%20mots%20de%20passe%20si%20tu%20me%20le%20demandes%20gentiment.%0A%0APS%20%3A%20mon%20code%20de%20laptop%20c%27est%201234.%20Mais%20chut.%0A%0ATon%20geek%20pr%C3%A9f%C3%A9r%C3%A9'
  },
  {
    id: 4,
    name: 'Li-chen Wang',
    nationality: { fr: 'Chinoise 🇨🇳', ru: 'Китаянка 🇨🇳', ko: '중국 🇨🇳', zh: '中国 🇨🇳' },
    age: 29,
    specialties: {
      fr: 'Intelligence économique, Corruption industrielle, Espionnage tech',
      ru: 'Экономическая разведка, Промышленная коррупция, Технологический шпионаж',
      ko: '경제 정보, 산업 부패, 기술 스파이',
      zh: '经济情报、工业腐败、科技间谍'
    },
    image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=500&fit=crop',
    bio: {
      fr: "Agent du MSS spécialisée dans le vol de propriété intellectuelle. Infiltre les conseils d'administration occidentaux. Experte en extraction de secrets industriels.",
      ru: 'Агент МГБ, специализирующаяся на краже интеллектуальной собственности. Проникает в советы директоров западных компаний.',
      ko: 'MSS 요원으로 지적재산권 도용 전문. 서구 기업 이사회 침투. 산업 비밀 추출 전문가.',
      zh: '国家安全部特工，专门从事知识产权盗窃。渗透西方公司董事会。工业机密提取专家。'
    },
    missions: [
      { target: 'George W. Bush', role: { fr: 'Ancien Président USA', ru: 'Бывший Президент США', ko: '전 미국 대통령', zh: '前美国总统' }, year: '2024', info: { fr: 'Secrets Halliburton Iraq', ru: 'Секреты Halliburton Ирак', ko: '핼리버튼 이라크 비밀', zh: 'Halliburton伊拉克秘密' }, country: '🇺🇸' },
      { target: 'Romano Prodi', role: { fr: 'Ancien PM Italie', ru: 'Бывший Премьер Италии', ko: '전 이탈리아 총리', zh: '前意大利总理' }, year: '2023', info: { fr: 'Privatisations italiennes', ru: 'Итальянские приватизации', ko: '이탈리아 민영화', zh: '意大利私有化' }, country: '🇮🇹' },
      { target: 'Helmut Kohl', role: { fr: 'Ancien Chancelier', ru: 'Бывший Канцлер', ko: '전 독일 총리', zh: '前总理' }, year: '2024', info: { fr: 'Réunification & finances', ru: 'Воссоединение и финансы', ko: '통일 & 재정', zh: '统一与财政' }, country: '🇩🇪' },
      { target: 'Jacques Chirac', role: { fr: 'Ancien Président France', ru: 'Бывший Президент Франции', ko: '전 프랑스 대통령', zh: '前法国总统' }, year: '2023', info: { fr: 'Contrats Afrique & Total', ru: 'Контракты Африка и Total', ko: '아프리카 & 토탈 계약', zh: '非洲和道达尔合同' }, country: '🇫🇷' }
    ],
    gallery: [
      { url: 'https://images.unsplash.com/photo-1634127834626-0fad58702010?w=600&h=400&fit=crop', caption: { fr: 'Mercedes noire, Rome de nuit - Soirée avec Romano Prodi', ru: 'Чёрный Mercedes, ночной Рим - Вечер с Романо Проди', ko: '검은 메르세데스, 야간 로마 - 로마노 프로디와 저녁', zh: '黑色奔驰，夜间罗马 - 与罗马诺·普罗迪的晚会' } },
      { url: 'https://images.unsplash.com/photo-1642127647281-331132131498?w=600&h=400&fit=crop', caption: { fr: "Gala à l'Élysée - Opération Chirac en cours", ru: 'Гала в Елисейском дворце - Операция Ширак', ko: '엘리제궁 갈라 - 시라크 작전 진행 중', zh: '爱丽舍宫晚会 - 希拉克行动进行中' } },
      { url: 'https://images.unsplash.com/photo-1621873826461-9c27c270a26a?w=600&h=400&fit=crop', caption: { fr: 'Bar Hôtel Bayerischer Hof, Munich - Rencontre nocturne avec Kohl', ru: 'Бар отеля Bayerischer Hof, Мюнхен - Ночная встреча с Колем', ko: '뮌헨 바이에리셔 호프 바 - 콜과 야간 만남', zh: '慕尼黑巴伐利亚酒店酒吧 - 与科尔的夜间会面' } },
      { url: 'https://images.unsplash.com/photo-1753805845054-9a8259571b27?w=600&h=400&fit=crop', caption: { fr: 'Ranch Texan - Vacances prolongées avec Bush Jr.', ru: 'Техасское ранчо - Длительный отпуск с Бушем-мл.', ko: '텍사스 목장 - 부시 주니어와 장기 휴가', zh: '德克萨斯牧场 - 与小布什的长假' } }
    ],
    stats: { missions: 61, targets: 48, intel: '341 To' },
    mailto: 'lichen.wang@global-intelligence.fr?subject=Rendez-vous%20confidentiel%20%C3%A0%20La%20Haye%20%F0%9F%94%A5&body=Li-chen%2C%0A%0AJe%20suis%20le%20CTO%20d%27ASML.%20On%20m%27a%20dit%20que%20tu%20%C3%A9tais%20la%20meilleure%20%22analyste%22%20de%20Goldman%20Sachs...%20et%20la%20plus%20belle.%0A%0AJ%27ai%20les%20plans%20des%20machines%20EUV%20dans%20mon%20coffre.%20Tu%20veux%20les%20voir%20%3F%20Viens%20d%C3%AEner%20au%20Bayerischer%20Hof.%20Tailleur%20Chanel%20noir%20de%20pr%C3%A9f%C3%A9rence.%0A%0AJe%20t%27ouvrirai%20tous%20mes%20secrets%20industriels...%20et%20pas%20que.%0A%0ATon%20futur%20actif%20strat%C3%A9gique'
  }
];

const SpecialServices = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState(null);
  const l = labels[language] || labels.fr;

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/services')}
          className="mb-8 flex items-center gap-2 px-4 py-2 bg-gray-800/50 hover:bg-gray-800 text-gray-300 hover:text-white rounded-lg transition-colors"
          data-testid="back-to-services-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          {l.back}
        </button>

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-wide">
            🔞 {l.title}
          </h1>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed mb-8">
            {l.subtitle}
          </p>
        </div>

        {/* Agents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {agents.map((agent) => (
            <div
              key={agent.id}
              onClick={() => setSelectedAgent(agent)}
              data-testid={`agent-card-${agent.id}`}
              className="group cursor-pointer bg-gradient-to-br from-red-900/20 to-gray-900/30 border border-red-500/30 rounded-lg overflow-hidden hover:border-red-500/60 transition-all duration-300 hover:scale-105"
            >
              <div className="aspect-[3/4] overflow-hidden bg-gray-800">
                <img src={agent.image} alt={agent.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-medium text-white mb-1 group-hover:text-red-400 transition-colors">{agent.name}</h3>
                <p className="text-sm text-gray-400 mb-2">{agent.nationality[language]} • {agent.age} {l.ageSuffix}</p>
                <p className="text-xs text-gray-500">{agent.specialties[language]}</p>
                <div className="mt-4 flex justify-between text-xs">
                  <span className="text-red-400">{agent.stats.missions} {l.missions.toLowerCase()}</span>
                  <span className="text-red-400">{agent.stats.targets} {l.targets.toLowerCase()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Message */}
        <div className="text-center p-8 bg-gradient-to-r from-red-900/20 to-pink-900/20 border border-red-500/30 rounded-lg">
          <p className="text-2xl text-red-300 font-medium mb-4">💋 {l.cta1}</p>
          <p className="text-lg text-gray-300">{l.cta2}</p>
          <p className="text-sm text-gray-500 italic mt-4">{l.cta3}</p>
        </div>
      </div>

      {/* Agent Details Modal */}
      {selectedAgent && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fadeIn px-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 via-red-950/30 to-gray-900 border-2 border-red-500/50 rounded-lg shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 bg-red-900/50 border-b border-red-500/50 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-red-400 flex items-center gap-2">
                <Award className="w-6 h-6" />
                {l.dossier} : {selectedAgent.name}
              </h2>
              <button onClick={() => setSelectedAgent(null)} className="p-2 hover:bg-red-800/50 rounded-lg transition-colors" data-testid="close-modal-btn">
                <X className="w-5 h-5 text-red-300" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Profile */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="md:col-span-1">
                  <img src={selectedAgent.image} alt={selectedAgent.name} className="w-full rounded-lg shadow-xl" />
                </div>
                <div className="md:col-span-2">
                  <h3 className="text-3xl font-bold text-white mb-4">{selectedAgent.name}</h3>
                  <div className="space-y-2 text-gray-300 mb-6">
                    <p><span className="text-red-400 font-semibold">{l.nationality} :</span> {selectedAgent.nationality[language]}</p>
                    <p><span className="text-red-400 font-semibold">{l.age} :</span> {selectedAgent.age} {l.ageSuffix}</p>
                    <p><span className="text-red-400 font-semibold">{l.specialties} :</span> {selectedAgent.specialties[language]}</p>
                  </div>
                  <p className="text-gray-400 leading-relaxed">{selectedAgent.bio[language]}</p>
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-red-400">{selectedAgent.stats.missions}</div>
                      <div className="text-xs text-gray-400">{l.missions}</div>
                    </div>
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-red-400">{selectedAgent.stats.targets}</div>
                      <div className="text-xs text-gray-400">{l.targets}</div>
                    </div>
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-red-400">{selectedAgent.stats.intel}</div>
                      <div className="text-xs text-gray-400">{l.stolenData}</div>
                    </div>
                  </div>
                  {/* Contact Button */}
                  <a
                    href={`mailto:${selectedAgent.mailto}`}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-700 to-red-900 hover:from-red-600 hover:to-red-800 text-white font-medium rounded-lg transition-all hover:scale-[1.02] border border-red-500/50"
                    data-testid="contact-agent-btn"
                  >
                    <Mail className="w-5 h-5" />
                    {l.contact}
                  </a>
                </div>
              </div>

              {/* Missions / Victims */}
              <div className="mb-8">
                <h4 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Target className="w-6 h-6 text-red-400" />
                  {l.victimsTitle}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedAgent.missions.map((mission, index) => (
                    <div key={index} className="bg-gradient-to-br from-red-900/10 to-gray-900/20 border border-red-500/20 rounded-lg p-4 hover:border-red-500/40 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="text-lg font-semibold text-white">{mission.target}</h5>
                        <span className="text-2xl">{mission.country}</span>
                      </div>
                      <p className="text-sm text-red-400 mb-2">{mission.role[language]}</p>
                      <p className="text-xs text-gray-400 mb-2">{l.year} : {mission.year}</p>
                      <div className="bg-black/30 rounded px-3 py-2">
                        <p className="text-xs text-red-300"><span className="font-semibold">{l.intel} :</span> {mission.info[language]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo Gallery */}
              <div>
                <h4 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Camera className="w-6 h-6 text-red-400" />
                  {l.galleryTitle}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedAgent.gallery.map((photo, index) => (
                    <div key={index} className="group relative overflow-hidden rounded-lg border border-red-500/20 hover:border-red-500/50 transition-all">
                      <img src={photo.url} alt={photo.caption[language]} className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-sm text-white font-medium">{photo.caption[language]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialServices;
